<template>
    <div class="sidebar-license-card">
        <div class="sidebar-license-card__header">
            <PhShieldCheck v-if="hasActiveLicense" :size="16" weight="fill" class="sidebar-license-card__header-icon text-amber-400" />
            <PhShieldWarning v-else :size="16" weight="fill" class="sidebar-license-card__header-icon text-zinc-500" />
            <span class="sidebar-license-card__title">{{ titleText }}</span>
            <button
                v-if="!hasActiveLicense || canUpgradeLicense"
                type="button"
                class="sidebar-license-card__upgrade"
                @click="handleUpgradeClick"
            >
                Upgrade
                <PhArrowRight :size="12" weight="bold" />
            </button>
        </div>
        <p v-if="employerProvidedBy" class="sidebar-license-card__provided" data-testid="employer-provided">
            Provided by {{ employerProvidedBy }}.
        </p>

        <ul class="sidebar-license-card__list">
            <li class="sidebar-license-card__item">
                <CheckIcon v-if="hasActiveLicense" class="sidebar-license-card__check" />
                <PhX v-else :size="16" class="sidebar-license-card__x" />
                <p>
                    <template v-if="hasActiveLicense"><span class="font-bold">Active</span> Protection</template>
                    <template v-else><span class="font-bold">Unprotected</span> &amp; Vulnerable</template>
                </p>
            </li>
            <li class="sidebar-license-card__item">
                <CheckIcon v-if="hasActiveLicense" class="sidebar-license-card__check" />
                <PhX v-else :size="16" class="sidebar-license-card__x" />
                <p><span class="font-bold">{{ formattedBrokerCount }}</span> Data Brokers</p>
            </li>
            <li class="sidebar-license-card__item">
                <CheckIcon v-if="hasActiveLicense" class="sidebar-license-card__check" />
                <PhX v-else :size="16" class="sidebar-license-card__x" />
                <p><span class="font-bold">{{ formattedCustomSiteCount }}</span> Custom Sites</p>
            </li>
            <li class="sidebar-license-card__item">
                <CheckIcon v-if="hasActiveLicense" class="sidebar-license-card__check" />
                <PhX v-else :size="16" class="sidebar-license-card__x" />
                <p><span class="font-bold">{{ formattedTimeSaved }}</span> Time Saved</p>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { CheckIcon } from '@heroicons/vue/24/outline';
import { PhShieldCheck, PhShieldWarning, PhArrowRight, PhX } from '@phosphor-icons/vue';
import { usePersonStore } from '~/stores/person';
import { useLicensePurchase } from '~/composables/useLicensePurchase';
import { isTopTierLicense, licenseTierName } from '~/types/licenses/tiers';

const personStore = usePersonStore();
const { person } = storeToRefs(personStore);
const auth = useAuthStore();
// One broker count for every app: the live public stats, already formatted.
const { formattedBrokerCount, formattedCustomSiteCount } = useBrokerStats();
const { formatTimeFromMinutes } = useUtils();
const { isUpgradeableLicense, employerContext } = useLicensePurchase();
const { hasActiveLicense, gate, promptForGate } = useLicenseAccess();

// A seat another member's plan extends (a claimed free license) names whose plan it rides on, so
// the member knows who covers them and that the seat is theirs to upgrade.
const titleText = computed(() => {
    if (!hasActiveLicense.value) return 'Unlicensed';

    const license = person.value?.license;
    const tier = licenseTierName(license?.slug);

    return license?.sponsored && license.sponsor_first_name ? `${tier} · extended by ${license.sponsor_first_name}` : tier;
});

// Whether this card can offer an upgrade at all. Two ways in, and they are different questions:
//
// - The loaded person owns the license, so checkout's modify flow will take it. That is the same
//   question checkout answers, asked in the one place that owns it.
// - Or the viewer is acting for the company, where the upgrade is a company purchase in the
//   business checkout rather than a modify of this license. `isUpgradeableLicense` rejects every
//   company-owned license BY DESIGN — the modify flow 403s one — so without this arm the control
//   was never rendered for exactly the case it is wanted in: an admin looking at an employee whose
//   company license is below the tier a feature needs.
//
// Top tier is excluded either way: there is nothing above it to move to.
const canUpgradeLicense = computed(() => {
    if (isUpgradeableLicense(person.value?.license)) return true;
    return !!employerContext.value && !isTopTierLicense(person.value?.license?.slug);
});

// An employer-provided license is owned by the company, so no Upgrade is offered; name who provides it
// instead of leaving a bare card. `owner === false` alone also covers a relative's gift, hence the company (type 2) test.
const employerProvidedBy = computed(() => {
    const owner = person.value?.license?.owner;
    if (!hasActiveLicense.value || auth.user?.license?.owner !== false || owner?.type !== 2) return null;
    return owner.name?.trim() || 'your employer';
});

// Time Saved belongs to the person on screen — under impersonation the person
// store already holds the member, so this is the member's number by construction.
const formattedTimeSaved = computed(() => formatTimeFromMinutes(timeSavedMinutes(person.value?.stats)));

const handleUpgradeClick = () => {
    if (!hasActiveLicense.value) {
        promptForGate(gate.value);
        return;
    }
    if (!canUpgradeLicense.value) return;
    // Both ways in go through the gate rather than being decided again here: it sends an employer to
    // the business checkout and a license the loaded person owns into checkout's modify flow, and a
    // card that chose between them itself would be a second rule to drift from that one.
    promptForGate('needs_upgrade');
};

defineExpose({ handleUpgradeClick });
</script>

<style lang="scss" scoped>
.sidebar-license-card {
    @apply mx-4 p-4 rounded-lg bg-rail-raised;
    box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);

    &__header {
        @apply flex items-center gap-2 pb-3 border-b border-zinc-700;
    }

    &__header-icon {
        @apply shrink-0;
    }

    &__title {
        @apply flex-1 text-sm font-bold text-ink-invert leading-5;
    }

    &__upgrade {
        @apply text-amber-400 text-xs font-semibold flex items-center gap-1 cursor-pointer bg-transparent border-0 hover:underline;
    }

    &__provided {
        @apply mt-3 mb-0 text-ink-invert text-[13px] leading-5;
    }

    &__list {
        @apply flex flex-col gap-2 mt-3 list-none p-0 m-0;
    }

    &__item {
        @apply flex items-center gap-2 text-ink-invert text-[13px] leading-5;

        p {
            @apply text-ink-invert;
            span {
                @apply text-ink-invert font-bold;
            }
        }
    }

    &__check {
        @apply h-4 w-4 text-amber-400 shrink-0;
    }

    &__x {
        @apply text-zinc-500 shrink-0;
    }

}
</style>
